import type * as amplitude from '@amplitude/analytics-browser';
import { reportError } from '~/lib/rum';
import { attachAmpli } from '~/domain/plugins/analytics';
import type { Ampli } from '~/domain/plugins/services/ampli';
import type { PluginInitContext } from '../types';
import { getPixels } from '../services/pixels.client';
import { getCheckoutAnalyticsContext } from './checkout-analytics-context';
import { clientLogger } from '~/lib/logger';
import { env } from '~/platform/platform';
import { describeRoute } from '~/lib/routing/route-registry';
import { getUReferrer } from '~/domain/plugins/services/tracker/persist-campaign.client';

const fetchSdk = () =>
  Promise.all([
    import('~/domain/plugins/services/ampli'),
    import('@amplitude/analytics-browser'),
  ]);
let sdk: ReturnType<typeof fetchSdk> | undefined;
const download = () => (sdk ??= fetchSdk());

/** An Amplitude identity handed over in the URL, e.g. by the payment return. */
export type LinkedIdentity = { deviceId?: string; sessionId?: number };

/**
 * Reads the identity params the way the SDK's own init does (analytics-browser
 * 2.44: `ampDeviceId`, else `deviceId`, and `ampSessionId`, all ignored once
 * an `ampTimestamp` has passed), so passing the result to `load()` changes
 * when they are read, not what they mean.
 */
export function readLinkedIdentity(
  search: string,
  now: number
): LinkedIdentity {
  const params = new URLSearchParams(search);
  const read = (name: string) => params.get(name) || undefined;

  // The SDK treats a missing, zero or unparseable timestamp as no limit.
  const expiresAt = Number(read('ampTimestamp'));
  if (expiresAt && now >= expiresAt) return {};

  const deviceId = read('ampDeviceId') ?? read('deviceId');
  const sessionId = Number(read('ampSessionId'));
  return {
    ...(deviceId && { deviceId }),
    ...(!Number.isNaN(sessionId) && { sessionId }),
  };
}

// The SDK reads these params from the URL only when it initialises, which is
// now after its download. By then the payment callback may have navigated to
// the receipt and dropped them, and `Booking Completed` would come from a new
// anonymous device. Nothing can navigate before this module evaluates, ahead
// of hydration; the expiry is checked then too, as the SDK did on arrival.
const linkedIdentity: LinkedIdentity =
  typeof window === 'undefined'
    ? {}
    : readLinkedIdentity(window.location.search, Date.now());

/**
 * Starts the SDK download, which nothing on screen needs, once the page is up.
 * Fetching code sets no cookies and sends nothing, so it need not wait for
 * consent, and overlapping that wait keeps `pluginsReady` from growing.
 */
export function preload(): void {
  // `init` handles a failed download, but only once consent is settled.
  if (env.AMPLITUDE_API_KEY) download().catch(() => undefined);
}

export async function init(ctx: PluginInitContext) {
  let loaded: Ampli | undefined;
  try {
    const apiKey = env.AMPLITUDE_API_KEY;
    if (!apiKey) {
      clientLogger.warn(
        '[amplitude] VITE_AMPLITUDE_API_KEY is not set, skipping init'
      );
      return;
    }

    const [{ ampli, DefaultConfiguration }, { Identify, Types }] =
      await download();

    await ampli.load({
      client: {
        apiKey: apiKey,
        configuration: {
          ...DefaultConfiguration,
          ...linkedIdentity,
          autocapture: {
            attribution: true,
            elementInteractions: false,
            pageUrlEnrichment: true,
            pageViews: true,
            sessions: true,
            webVitals: false,
          },
          logLevel: Types.LogLevel.Warn,
        },
      },
    }).promise;
    loaded = ampli;

    ampli.client.add(pageNameEnrichment());
    ampli.client.add(checkoutPropertiesEnrichment());
    ampli.client.add(uReferrerEnrichment());

    // Apply user properties WITHOUT setting a user_id. Anonymous storefront
    // visitors must stay device_id-based (Amplitude advises against fabricating
    // a user_id for anonymous users); passing `undefined` leaves userId unset.
    ampli.identify(undefined, {
      ...getPixels(),
      company_name: ctx.companyName,
      company_id: ctx.companyId,
    });

    const uReferrer = getUReferrer();
    if (uReferrer) {
      ampli.client.identify(
        new Identify().setOnce('initial_u_referrer', uReferrer)
      );
    }

    /** TODO: Deprecated.
     *  Using the autocapture amplitude config, there's a native way to track device size.
     *  These properties are kept for backwards compatible,
     */
    if (typeof window !== 'undefined' && window.visualViewport) {
      const { width, height } = window.visualViewport;
      ampli.identify(undefined, {
        'Device viewport width': width,
        'Device viewport height': height,
        'Device viewport': `${width}x${height}`,
      });
    }
  } catch (error) {
    clientLogger.warn(error, '[amplitude] Failed to initialize');
    // The SDK download can fail on its own, which costs the page every product
    // event, and a warning never leaves the visitor's browser.
    reportError({
      name: 'AmplitudeInitFailed',
      message: error instanceof Error ? error.message : String(error),
    });
  } finally {
    // Once loaded, Amplitude sends events even if enriching or identifying
    // failed; only the properties those steps add are missing.
    if (loaded) attachAmpli(loaded);
  }
}

const pageNameEnrichment = (): amplitude.Types.EnrichmentPlugin => {
  return {
    name: 'understory-page-view-tracking-enrichment',
    type: 'enrichment',
    setup: async () => undefined,
    execute: async (event) => {
      if (event.event_type !== '[Amplitude] Page Viewed') {
        return event;
      }

      const rawPath = (
        event.event_properties as { [key: string]: string } | undefined
      )?.['[Amplitude] Page Path'];
      if (typeof rawPath === 'string') {
        // Collapse high-cardinality ids to the route pattern and attach the
        // human page name. Single source of truth: `~/lib/routing/route-registry`.
        const { pattern, name } = describeRoute(rawPath);
        event.event_properties = {
          ...event.event_properties,
          page_name: name,
          page_path_pattern: pattern,
        };
      }
      return event;
    },
  };
};

/**
 * Stamps `experience_id` + `booking_mode` (global tracking-plan properties)
 * onto every `Checkout *` event from the checkout analytics context.
 */
const checkoutPropertiesEnrichment = (): amplitude.Types.EnrichmentPlugin => {
  return {
    name: 'understory-checkout-properties-enrichment',
    type: 'enrichment',
    setup: async () => undefined,
    execute: async (event) => {
      if (!event.event_type.startsWith('Checkout ')) {
        return event;
      }
      const { experienceId, bookingMode: pendingMode } =
        getCheckoutAnalyticsContext();
      // An event fired before a streamed booking mode lands waits for it. The
      // router settles every streamed value, by its stream timeout at worst.
      const bookingMode = await Promise.resolve(pendingMode).catch(
        () => undefined
      );
      event.event_properties = {
        ...event.event_properties,
        ...(experienceId ? { experience_id: experienceId } : {}),
        ...(bookingMode ? { booking_mode: bookingMode } : {}),
      };
      return event;
    },
  };
};

// Amplitude has no session properties, so tagging every event is how a session
// is attributed to the widget it started in.
const uReferrerEnrichment = (): amplitude.Types.EnrichmentPlugin => {
  return {
    name: 'understory-u-referrer-enrichment',
    type: 'enrichment',
    setup: async () => undefined,
    execute: async (event) => {
      const uReferrer = getUReferrer();
      // `$identify` and friends carry user properties, not event properties.
      if (uReferrer && !event.event_type.startsWith('$')) {
        event.event_properties = {
          ...event.event_properties,
          u_referrer: uReferrer,
        };
      }
      return event;
    },
  };
};

export { checkoutPropertiesEnrichment, uReferrerEnrichment };
